import { CID } from 'multiformats/cid';

import { createAppAsyncThunk } from '@/store/thunks/createAppAsyncThunk';
import { enqueueFile } from '@/store/slices/poc/cloneQueueSlice';
import type { FileMetadata } from '@/types/poc/helia';

import { abortCurrentClone } from './processCloneQueue';
import { processCloneQueue } from './index';

const POLL_INTERVAL_MS = 500;
const MAX_WAIT_MS = 300_000; // 5 minutes

// eslint-disable-next-line promise/avoid-new -- No alternative for setTimeout-based async delay
const delay = async(ms: number): Promise<void> => { await new Promise<void>(resolve => { setTimeout(resolve, ms); }); };

type EnsureResult =
  | { source: 'already-local' }
  | { source: 'cloned' }
  | { source: 'waited' };

/**
 * Ensure a file is fully cloned to the local Helia blockstore before proceeding.
 *
 * - If already pinned locally → resolves immediately.
 * - If the same CID is currently cloning → waits for it to finish.
 * - If a different CID is cloning → aborts that clone (re-queued), enqueues
 *   target with user-initiated priority, and waits.
 * - If not in the system at all → enqueues and waits.
 */
export const ensureFileCloned = createAppAsyncThunk(
  'cloneQueue/ensureFileCloned',
  async(
    { sixId, fileMetadata }: { sixId: string; fileMetadata: FileMetadata },
    { dispatch, getState, extra: { pocServices, loggerService } }
  ): Promise<EnsureResult> => {
    const log = loggerService.createLogger({ namespace: 'six3ro:p2p:ensure-cloned' });
    const cid = fileMetadata.cid;

    // 1. Check if file is already pinned locally
    const helia = pocServices.p2pStackManagerService.getHeliaNode(sixId);
    if (helia != null) {
      const parsedCid = CID.parse(cid);
      const pinned = await helia.helia.pins.isPinned(parsedCid);
      if (pinned) {
        log.debug('File already pinned locally:', cid);
        return { source: 'already-local' };
      }
    }

    // 2. Check current clone queue state
    const state = getState();
    const { currentlyCloning } = state.cloneQueue;

    if (currentlyCloning?.cid === cid) {
      // Same file is already being cloned — just wait for it
      log.debug('File is already being cloned, waiting:', cid);
    } else {
      // Different file or nothing cloning — enqueue with user-initiated priority
      if (currentlyCloning != null) {
        log.info('Interrupting current clone of', currentlyCloning.cid, 'for', cid);
        abortCurrentClone();
      }

      dispatch(enqueueFile({
        sixId,
        cid,
        metadata: fileMetadata,
        size: fileMetadata.size,
        priority: 'user-initiated',
      }));

      void dispatch(processCloneQueue());
    }

    // 3. Poll until file is cloned or failed
    const deadline = Date.now() + MAX_WAIT_MS;

    while (Date.now() < deadline) {
      await delay(POLL_INTERVAL_MS);

      const current = getState();

      // Success: file appeared in helia.files (addFileMetadata was dispatched)
      if (cid in current.helia.files) {
        log.info('File clone confirmed:', cid);
        return { source: currentlyCloning?.cid === cid ? 'waited' : 'cloned' };
      }

      // Failure: file ended up in failedFiles
      if (cid in current.cloneQueue.failedFiles) {
        throw new Error(current.cloneQueue.failedFiles[cid]);
      }
    }

    throw new Error(`Timeout: file ${cid} did not clone within ${MAX_WAIT_MS}ms`);
  }
);
